import { createSignal } from 'solid-js';
import { useApi, isOk } from '../api';
import { firstError } from '../api/envelope';
import type { SetupModeArmRequest, SetupModeValueDto } from '../api/generated/schemas';

// One capture runs at a time, so one poll serves every picker on the page rather than each button
// running its own. Captures are addressed by field id: the backend hands a value out once, and this
// is what routes it to the input that asked for it.
const POLL_MS = 2000;

export interface SetupModeFieldHandlers {
    onCaptured: (value: SetupModeValueDto) => void;
    onEnded: (reason: string) => void;
}

type Api = ReturnType<typeof useApi>;

const listeners = new Map<string, SetupModeFieldHandlers>();
const [armedField, setArmedField] = createSignal<string | null>(null);

// useApi reads the user context, which only resolves during a component's setup. The poll loop and
// every awaited continuation run outside that, so the facade is captured once while a picker mounts.
let api: Api | null = null;
let pollHandle: number | undefined;
let reclaimed = false;

export const setupModeArmedField = armedField;

export function registerSetupModeField(fieldId: string, handlers: SetupModeFieldHandlers, boundApi: Api): () => void {
    api = boundApi;
    listeners.set(fieldId, handlers);
    scheduleReclaim();
    return () => {
        listeners.delete(fieldId);
        if (listeners.size === 0) stopPolling();
    };
}

export interface SetupModeArmFailure {
    /** The backend's error key, which the panel renders in the operator's own language. */
    key: string;
    message: string;
}

/** Arms a capture. Returns null on success, or why it could not start. */
export async function armSetupMode(request: SetupModeArmRequest): Promise<SetupModeArmFailure | null> {
    if (!api) return null;
    const res = await api.setupMode.arm(request);
    if (!isOk(res.data)) {
        const key = Object.keys(res.data?.errors ?? {})[0] ?? '';
        return { key, message: firstError(res.data) ?? '' };
    }
    // isOk only reads the envelope status, so a 200 carrying no value still has to be handled.
    if (!res.data.value) return null;

    setArmedField(res.data.value.fieldId);
    startPolling();
    return null;
}

export async function cancelSetupMode(): Promise<void> {
    if (!api) return;
    await api.setupMode.cancel();
    setArmedField(null);
    stopPolling();
}

// A tab that reloaded mid-capture still has the value waiting for it, so the first poll happens once
// every field on the page has had a chance to register rather than on the first one to mount.
function scheduleReclaim(): void {
    if (reclaimed) return;
    reclaimed = true;
    window.setTimeout(() => { void poll(); }, 0);
}

function startPolling(): void {
    if (pollHandle !== undefined) return;
    pollHandle = window.setInterval(() => {
        if (document.visibilityState === 'visible') void poll();
    }, POLL_MS);
}

function stopPolling(): void {
    if (pollHandle === undefined) return;
    clearInterval(pollHandle);
    pollHandle = undefined;
}

async function poll(): Promise<void> {
    if (!api) return;
    const res = await api.setupMode.pending();
    if (!isOk(res.data) || !res.data.value) return;

    const pending = res.data.value;

    if (pending.capture) {
        setArmedField(null);
        stopPolling();
        listeners.get(pending.capture.fieldId)?.onCaptured(pending.capture.value);
        return;
    }

    if (pending.endedReason) {
        const ended = armedField();
        setArmedField(null);
        stopPolling();
        if (ended) listeners.get(ended)?.onEnded(pending.endedReason);
        return;
    }

    if (pending.armed) {
        setArmedField(pending.armed.fieldId);
        startPolling();
        return;
    }

    if (armedField() !== null) {
        setArmedField(null);
        stopPolling();
    }
}
